import classnames from 'classnames'
import { css } from 'emotion'
import {
  LoanSortOption,
  LoanSortOptionValues,
  LoanSortOptionWithAmountValues,
  LoanSortOptionWithClosestAmountValues,
  LoanSortOptionWithConversionProbabilityValues,
  LoanStep,
  RedirectType,
  SortDirection,
} from 'even-ts-static'
import * as React from 'react'

import SortOptions, { SortStyles } from 'embed/components/SortOptions'
import { LoanOfferProductSubType, TRANSUNION_COMPANY_UUID } from 'embed/constants'
import { LoanOffer } from 'embed/constants/models'
import { AprVsRecSorting, SortByClosestLoanAmount } from 'embed/experiments'
import { useTheme } from 'embed/hooks'
import AdvertiserDisclosure from '../AdvertiserDisclosure'
import AprTypeToggleFilter from '../AprTypeToggleFilter'
import ListItem from 'ui/multi-product/components/Loan/ListItem'
import { LoanOffersByLender } from 'embed/components/LoanOfferList/container'
import {
  bannerText,
  DEFAULT_PERSONAL_LOAN_OFFER_TOOLTIP_TEXT,
  productSubTypeIconSet,
} from 'embed/components/OfferBanner'
import { Auto, BasicLoan, Loan, SLR } from 'embed/components/Loan/Application/loan-variant'

const resultsStyles = require('embed/styles/results')

export type DisclaimerByProductSubType = { [key in LoanOfferProductSubType]: string }

export interface Props {
  allLoanOffersByLender: { [s: string]: LoanOffer[] }
  bestOfferId?: string
  companyUuid: string
  ctaButtonText: string
  ctaLabel: string
  ctaPreQualButtonText?: string
  currentStep: string
  dispatchOfferCta: (loan: LoanOffer, redirectType?: RedirectType) => void
  displaySortBarItems?: boolean
  isDotDash: boolean
  isResultsView: boolean
  loanOffers: LoanOffer[]
  loanVariant: Loan
  offerListRef: React.Ref<HTMLOListElement>
  onSeeMoreFromLenderClick: (offer: LoanOffer) => void
  onSortOptionClick: (sortOption: LoanSortOption) => void
  selectedAprVsRecSortingVariantId?: string
  selectedSortByClosestLoanAmountVariantId?: string
  showDefaultSortOption?: boolean
  showSeeMoreOffers?: boolean
  sort: LoanSortOption
  sortDirection: SortDirection
  customSortStyles?: SortStyles
  viewportHeight?: number
  viewportWidth?: number
}

const getSortOptionValues = (
  currentStep: string,
  isResultsView: boolean,
  selectedAprVsRecSortingVariantId?: string,
  selectedSortByClosestLoanAmountVariantId?: string,
) => {
  if (selectedAprVsRecSortingVariantId === AprVsRecSorting.variants.ConversionProbability.id) {
    return LoanSortOptionWithConversionProbabilityValues
  } else if (currentStep === LoanStep.OfferList) {
    return LoanSortOptionValues
  } else if (
    isResultsView &&
    selectedSortByClosestLoanAmountVariantId === SortByClosestLoanAmount.variants.SortByClosestAmount.id
  ) {
    return LoanSortOptionWithClosestAmountValues
  } else {
    return LoanSortOptionWithAmountValues
  }
}

const isTransunionSupply = (companyUuid: string) => companyUuid === TRANSUNION_COMPANY_UUID

const getTransunionDisclaimer = (colorTextSecondary: string) => (
  <span
    className={classnames(
      resultsStyles.notFirmOffers,
      css`
        color: ${colorTextSecondary};
      `,
    )}
  >
    These are not firm offers of credit |
  </span>
)

const DefaultSections = [
  LoanOfferProductSubType.PersonalLoan,
  LoanOfferProductSubType.SecuredLoan,
  LoanOfferProductSubType.LineOfCredit,
]
const AutoRefiSections = [
  LoanOfferProductSubType.AutomobileRefinance,
  LoanOfferProductSubType.PersonalLoan,
  LoanOfferProductSubType.SecuredLoan,
  LoanOfferProductSubType.LineOfCredit,
]
const SpecialOffersSections = [LoanOfferProductSubType.DebtRelief]

const getDefaultSections = (loanVariant: Loan) => {
  switch (loanVariant.kind) {
    case BasicLoan().kind:
    case SLR().kind:
      return DefaultSections
    case Auto().kind:
      return AutoRefiSections
    default:
      return [] as never
  }
}

export const getSectionsOrder = (loanVariant: Loan, loanOffers: LoanOffer[]) => {
  if (hideSpecialOffers(loanOffers)) {
    return getSections(loanVariant, loanOffers)
  } else {
    return SpecialOffersSections
  }
}

export const getSections = (loanVariant: Loan, loanOffers: LoanOffer[]) => {
  const starterList = getDefaultSections(loanVariant)
  const subTypesWithOffers = getSectionTypesWithOffers(loanOffers)

  const sections = starterList.reduce(
    (reducedSections: LoanOfferProductSubType[], subType: LoanOfferProductSubType) => {
      if (subTypesWithOffers[subType]) {
        reducedSections.push(subType)
      }
      return reducedSections
    },
    [],
  )

  if (loanVariant === Auto() && subTypesWithOffers[LoanOfferProductSubType.AutomobileRefinance]) {
    return [LoanOfferProductSubType.AutomobileRefinance].concat(
      sections.filter((subType) => subType !== LoanOfferProductSubType.AutomobileRefinance),
    )
  }
  return sections
}

const hideSpecialOffers = (loanOffers: LoanOffer[]) =>
  // If there are any offers and they aren't DebtRelief or Unknown then hide special offers
  loanOffers.length &&
  loanOffers.find(
    (offer: LoanOffer) =>
      ![LoanOfferProductSubType.DebtRelief, LoanOfferProductSubType.Unknown].includes(offer.productSubType),
  )

const getDisclaimerByProductSubType = (loanOffers: LoanOffer[]) =>
  loanOffers.reduce((disclaimers: DisclaimerByProductSubType, loanOffer: LoanOffer) => {
    disclaimers[loanOffer.productSubType] = disclaimers[loanOffer.productSubType] ?? loanOffer.productSubTypeDisclaimer
    return disclaimers
  }, {} as DisclaimerByProductSubType)

const getGroupedResultsByProductSubType = (loanOffers: LoanOffer[]) =>
  loanOffers.reduce((groupedOffers: LoanOffersByLender, offer: LoanOffer) => {
    if (!groupedOffers[offer.productSubType]) {
      groupedOffers[offer.productSubType] = []
    }
    groupedOffers[offer.productSubType].push(offer)
    return groupedOffers
  }, {} as LoanOffersByLender)

const getSectionTypesWithOffers = (loanOffers: LoanOffer[]) =>
  loanOffers.reduce((offerTypes: { [key in LoanOfferProductSubType]: boolean }, offer: LoanOffer) => {
    offerTypes[offer.productSubType] = true
    return offerTypes
  }, {} as { [key in LoanOfferProductSubType]: boolean })

export const groupedResultsByProductSubTypeHandler = (
  groupedResultsByProductSubType: LoanOffersByLender,
  subTypeKey: LoanOfferProductSubType,
) => {
  // The section for special offers is under debt relief, but needs offers from debt relief and/or unknown
  if (subTypeKey === LoanOfferProductSubType.DebtRelief) {
    return [LoanOfferProductSubType.DebtRelief, LoanOfferProductSubType.Unknown].reduce(
      (offers, subType) => offers.concat(groupedResultsByProductSubType[subType] ?? []),
      [] as LoanOffer[],
    )
  }
  return groupedResultsByProductSubType[subTypeKey] ?? []
}

export const NoOfferText = "We don't have any offers of this type for you."

// eslint-disable-next-line complexity
export const GroupedLoanOfferList: React.FC<Props> = ({
  allLoanOffersByLender,
  companyUuid,
  bestOfferId,
  currentStep,
  customSortStyles,
  dispatchOfferCta,
  displaySortBarItems,
  isDotDash,
  isResultsView,
  loanOffers,
  loanVariant,
  offerListRef,
  onSeeMoreFromLenderClick,
  onSortOptionClick,
  selectedAprVsRecSortingVariantId,
  selectedSortByClosestLoanAmountVariantId,
  showDefaultSortOption,
  showSeeMoreOffers,
  sort,
  sortDirection,
  viewportHeight,
  viewportWidth,
}) => {
  const shouldDisplayTransunionDisclaimer = isTransunionSupply(companyUuid) && isResultsView
  const sortOptionValues = getSortOptionValues(
    currentStep,
    isResultsView,
    selectedAprVsRecSortingVariantId,
    selectedSortByClosestLoanAmountVariantId,
  )
  const hasOffers = loanOffers.length > 0
  const { colorTextSecondary, fontSizeBase } = useTheme()

  const disclaimerByProductSubType = getDisclaimerByProductSubType(loanOffers)
  const groupedResultsByProductSubType = getGroupedResultsByProductSubType(loanOffers)

  const groupOrder = getSectionsOrder(loanVariant, loanOffers)
  return (
    <div>
      {!hasOffers && (
        <h2 className={resultsStyles.emptyResults}>We couldn't find any offers matching the selected criteria.</h2>
      )}
      {hasOffers && (
        <React.Fragment>
          <div className={resultsStyles.sortBarContainer}>
            <div className={resultsStyles.sortFilterContainer}>
              <SortOptions
                customStyles={customSortStyles}
                sortOptionValues={sortOptionValues}
                sort={sort}
                sortDirection={sortDirection}
                onSortOptionClick={onSortOptionClick}
                showDefaultSortOption={showDefaultSortOption}
              />
              {displaySortBarItems && viewportWidth && viewportWidth > 500 && (
                <AprTypeToggleFilter styles={{ toggleFilterListContainer: resultsStyles.sortBarAprTypeFilter }} />
              )}
            </div>
            {displaySortBarItems && (
              <div>
                {shouldDisplayTransunionDisclaimer && getTransunionDisclaimer(colorTextSecondary)}
                <AdvertiserDisclosure
                  className={classnames(resultsStyles.sortBarDisclosure, {
                    [css`
                      position: absolute;
                    `]: isDotDash,
                  })}
                />
              </div>
            )}
          </div>
          {hasOffers && viewportWidth && viewportWidth <= 500 && (
            <div className={resultsStyles.list}>
              {displaySortBarItems && (
                <AprTypeToggleFilter styles={{ toggleFilterListContainer: resultsStyles.emptyResults }} />
              )}
            </div>
          )}
          {groupOrder.map((subTypeKey, index) => {
            const subTypeOffers: LoanOffer[] = groupedResultsByProductSubTypeHandler(
              groupedResultsByProductSubType,
              subTypeKey,
            )
            const headerText = bannerText[subTypeKey]
            const HeaderElement = productSubTypeIconSet[subTypeKey]

            return (
              <React.Fragment key={subTypeKey}>
                <div
                  className={classnames(
                    resultsStyles.offerListHeaderContainer,
                    css`
                      padding-top: ${index > 0 ? '5px' : '25px'};
                    `,
                  )}
                >
                  <div
                    className={classnames(
                      resultsStyles.headerTitle,
                      css`
                        font-size: ${fontSizeBase};
                      `,
                    )}
                  >
                    {HeaderElement}
                    <span>{headerText}</span>
                  </div>
                  <div
                    className={classnames(
                      resultsStyles.headerDescription,
                      css`
                        color: ${colorTextSecondary};
                        font-size: ${fontSizeBase};
                      `,
                    )}
                  >
                    {subTypeOffers.length > 0 && (
                      <span>{disclaimerByProductSubType[subTypeKey] || DEFAULT_PERSONAL_LOAN_OFFER_TOOLTIP_TEXT}</span>
                    )}
                    {!(subTypeOffers.length > 0) && <span>{NoOfferText}</span>}
                  </div>
                </div>
                {subTypeOffers.length > 0 && (
                  <ol ref={offerListRef} className={resultsStyles.list}>
                    {subTypeOffers.map((offer) => {
                      const lenderCompanyUuid = offer.originator?.companyUuid || ''
                      const hasMoreOffers =
                        allLoanOffersByLender[lenderCompanyUuid] && allLoanOffersByLender[lenderCompanyUuid].length > 0
                      const tooltipText = offer.productSubTypeDisclaimer ?? DEFAULT_PERSONAL_LOAN_OFFER_TOOLTIP_TEXT
                      return (
                        <ListItem
                          bestOfferId={bestOfferId}
                          dispatchOfferCta={dispatchOfferCta}
                          dispatchSeeMoreFromLender={onSeeMoreFromLenderClick}
                          hasMoreOffers={hasMoreOffers}
                          isResultsView={isResultsView}
                          key={offer.uuid}
                          offer={offer}
                          tooltipText={tooltipText}
                          shouldDisplayTransunionDisclaimer={!!shouldDisplayTransunionDisclaimer}
                          showSeeMoreOffers={showSeeMoreOffers}
                          viewportHeight={viewportHeight}
                          viewportWidth={viewportWidth}
                        />
                      )
                    })}
                  </ol>
                )}
              </React.Fragment>
            )
          })}
        </React.Fragment>
      )}
    </div>
  )
}
